import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Image, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../store";
import { getVendorUserById, loginUser } from "../store/auth/authActions";
import { signIn, signOut, getCurrentUser } from "aws-amplify/auth";
import { api_url } from '../api/config/config';
import { LoginResponse } from "../store/auth/types";
import Footer from "../components/Footer";

const LoginPage: React.FC = () => {
    const backendURL = api_url;
    const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';
    const dispatch = useDispatch<AppDispatch>();
    const authState = useSelector((state: RootState) => state.auth); // Access the auth state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const navigate = useNavigate();

    const currentAuthenticatedUser = useCallback(async () => {
        try {
            const { username, userId, signInDetails } = await getCurrentUser();
            navigate("/mypage");
        } catch (err) {
            console.log(err);
        }
    }, [navigate])

    useEffect(() => {
        currentAuthenticatedUser()
    }, [currentAuthenticatedUser])

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoggingIn(true);
        try {
            const resultAction = await dispatch(loginUser({ email, password }));

            // 2. 成功かどうか確認
            if (loginUser.fulfilled.match(resultAction)) {
                // 成功時
                const userId = resultAction.payload.data.user.id;
                const vendorUser = getVendorUserById({ id: userId });
                if (vendorUser) {
                    await dispatch(vendorUser);
                    navigate('/mypage');
                }
                
                // ログイン履歴を送信
                fetch(`${api_url}/writeLoginLog`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user: resultAction.payload.data.user }),
                    keepalive: true,
                  }).catch(err => console.error('ログイン履歴送信失敗', err));
            } else if (loginUser.rejected.match(resultAction)) {
                const errorMessage = resultAction.payload || 'ログインに失敗しました。';
                throw new Error(errorMessage);
            }
        } catch (error: any) {
            alert(error.message || 'ログイン処理で例外発生しました。');
            console.error('ログイン処理で例外発生:', error);
            await signOut();
        } finally {
            setIsLoggingIn(false);
        }
    };

    return (
        <div>
            <section className="registration-nav"></section>
            <section className="section-login login-bg-img">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-5 col-md-6 mx-auto" style={isMaintenanceMode ? { display: 'none' } : {}}>
                            <div className="login-wrapper bg-white rounded-50 theme-box-shadow-2 p-lg-5 p-4 text-center">
                                <Image className="login-logo mb-5" src="assets/images/login/login-logo.svg" />
                                <Form id="loginForm" className="form-theme" method="post" onSubmit={handleSubmit}>
                                    <Form.Group className="form-group">
                                        <Form.Control
                                            id="email" type="text"
                                            name="email" className="form-control mb-2"
                                            placeholder="メールアドレス" required
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="form-group mb-4">
                                        <Form.Control
                                            id="password" type="password"
                                            name="password" className="form-control"
                                            placeholder="パスワード" required
                                            onChange={(e) => setPassword(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Button
                                        type="submit"
                                        className="btn btn-theme btn-danger rounded-pill px-lg-5 px-4 mb-5"
                                        disabled={isLoggingIn}
                                    >
                                        {isLoggingIn ? (
                                            <span style={{ display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    style={{ verticalAlign: "middle", marginRight: "0.5em" }}
                                                />
                                                <span style={{ verticalAlign: "middle" }}>ログイン中</span>
                                            </span>
                                        ) : (
                                            "ログイン"
                                        )}
                                    </Button>
                                    <div className=""><a href="forgot_password" className="text-link">パスワードを忘れた方はこちら ≫</a></div>
                                </Form>
                            </div>
                        </div>

                        <div className="col-lg-9 col-md-8 mx-auto" style={isMaintenanceMode ? {} : { display: 'none' }}>
                            <h2 className="mb-4 text-center" style={{ fontWeight: 700, fontSize: "1.7rem" }}>システムメンテナンスのお知らせ</h2>

                            <div className="text-md text-center" style={{ fontSize: "1.1rem", lineHeight: 2 }}>
                                <p>
                                    日頃よりENEBIDをご利用いただき誠にありがとうございます。<br />
                                    現在、ENEBIDのリニューアルに伴い、システムメンテナンスを実施しております。<br />
                                    ご不便、ご迷惑をお掛けいたしますことを深くお詫び申し上げます。
                                </p>
                                <p className="fw-bold mb-1">■サービス停止日時</p>
                                <p className="mb-3">
                                    2025年8月22日（金）18:00 ～ 2025年8月25日（月）10:00<br />
                                    <span>※作業進捗により、前後する場合がございます。ご了承ください。</span>
                                </p>
                                <p>
                                    何卒ご理解の程宜しくお願い致します。<br />
                                    メンテナンスに関するお問い合わせは、以下宛までご連絡下さい。
                                </p>
                                <hr className="my-4" />
                                <div style={{ fontSize: "1em", lineHeight: 1.8, }}>
                                    <strong>ENEBID カスタマーセンター</strong><br />
                                    〒150-0043<br />
                                    東京都渋谷区道玄坂1-19-2スプラインビル6F<br />
                                    Tel：03-5656-7260<br />
                                    Fax：03-5539-4563<br />
                                    Email：<a href="mailto:enebid@enecloud.co.jp">enebid@enecloud.co.jp</a><br />
                                    URL：<a href="https://enebid.com/" target="_blank" rel="noopener noreferrer">https://enebid.com/</a>
                                </div>
                            </div>

                        </div>



                    </div>
                </div>
            </section>
            <section className="section-login-contents text-center">
                <div className="container">
                    <div className="row mx-0">
                        <div className="col-lg-10 px-lg-4 px-0 mx-auto mt-lg-4 mt-2">
                            <Image className="mb-2" src="assets/images/other/icon-light-bg-white-circle-red.svg" />
                            <h3 className="text-md mb-4">Ene Bid とは</h3>
                            <p className="mb-lg-5 mb-sm-4 mb-3 text-sm text-left mr-lg-3">電力供給契約を変更検討中のお客様情報をデータベース化し、
                                電力会社の方へ連携することで自由競争を促す自動応札システムです。</p>
                        </div>
                    </div>
                </div>
            </section>
            <section className="section-login-contents bg-ass-06 text-center">
                <div className="container">
                    <div className="row mx-0">
                        <div className="col-lg-10 px-lg-4 px-0 mx-auto mt-lg-4 mt-2">
                            <Image className="mb-2" src="assets/images/other/icon-trophy-bg-white-circle-red.svg" />
                            <h3 className="text-md mb-5">３つのメリット</h3>
                            <div className="row">
                                <div className="col-md-4 text-center row-eq-height">
                                    <div className="login-item bg-white">
                                        <Image className="mr-4" src="assets/images/login/user-chart.svg" />
                                        <p className="text-sm fw-400">契約を見直したい<br />新規顧客を探せる</p>
                                    </div>
                                </div>
                                <div className="col-md-4 text-center row-eq-height">
                                    <div className="login-item bg-white">
                                        <Image src="assets/images/login/user-office.svg" />
                                        <p className="text-sm fw-400">他社に切り替え検討中の<br />契約者がわかる</p>
                                    </div>
                                </div>
                                <div className="col-md-4 text-center row-eq-height">
                                    <div className="login-item bg-white">
                                        <Image src="assets/images/login/clip-list.svg" />
                                        <p className="text-sm fw-400">応札条件を<br />柔軟に決められる</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />

        </div>
    )
}

export default LoginPage;